/* Lector de .zip en el navegador, para Importar documentos.
 *
 * Google Drive entrega una carpeta descargada como uno o varios .zip (los
 * parte cada ~2 GB). Descomprimirlos a mano es un paso más donde el abogado se
 * pierde, así que se leen directo.
 *
 * Sin librerías: se lee el índice del .zip (el "directorio central", al final
 * del archivo) con File.slice, y cada documento se descomprime recién cuando
 * se va a subir, con DecompressionStream del navegador. Así un .zip de 2 GB no
 * se carga entero en memoria: en cada momento solo está el documento que se
 * está subiendo.
 *
 * Soporta ZIP64 (los .zip de más de 4 GB o con más de 65.535 archivos) y los
 * nombres en UTF-8 o en la codificación vieja de Windows (CP437), que es como
 * llegan las tildes y la ñ de un .zip hecho en un PC.
 */

export type EntradaZip = { ruta: string; tamano: number; leer: () => Promise<Blob> };

export class ErrorZip extends Error {}

const FIRMA_FIN = 0x06054b50;
const FIRMA_FIN64 = 0x06064b50;
const FIRMA_LOCALIZADOR64 = 0x07064b50;
const FIRMA_CENTRAL = 0x02014b50;
const FIRMA_LOCAL = 0x04034b50;

/** Los bytes de un Blob. Con FileReader de respaldo: el entorno de pruebas
 *  (jsdom) no tiene Blob.arrayBuffer. */
export const bytesDe = (b: Blob): Promise<ArrayBuffer> =>
  typeof b.arrayBuffer === 'function'
    ? b.arrayBuffer()
    : new Promise((ok, mal) => { const r = new FileReader(); r.onload = () => ok(r.result as ArrayBuffer); r.onerror = () => mal(r.error); r.readAsArrayBuffer(b); });
const leerBytes = async (b: Blob, desde: number, hasta: number) => new DataView(await bytesDe(b.slice(desde, hasta)));
const u64 = (v: DataView, o: number) => Number(v.getBigUint64(o, true));

/** CP437, la página de códigos de los .zip de Windows, de 0x80 a 0xFF. */
const CP437 = 'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ';

function nombre(bytes: Uint8Array, utf8: boolean): string {
  if (utf8) return new TextDecoder('utf-8').decode(bytes);
  // Sin la marca de UTF-8: muchos programas igual escriben UTF-8 (el de Mac,
  // el de Google Drive). Si no es UTF-8 válido, es CP437.
  try { return new TextDecoder('utf-8', { fatal: true }).decode(bytes); }
  catch { return Array.from(bytes, c => (c < 0x80 ? String.fromCharCode(c) : CP437[c - 0x80])).join(''); }
}

/** Los archivos que hay dentro de un .zip (sin las carpetas), listos para
 *  leerse uno por uno. */
export async function leerZip(zip: Blob): Promise<EntradaZip[]> {
  // El registro final está en los últimos 22 bytes, más un comentario de
  // hasta 64 KB: se busca hacia atrás.
  const cola = Math.min(zip.size, 22 + 0xffff);
  const fin = await leerBytes(zip, zip.size - cola, zip.size);
  let pos = -1;
  for (let i = fin.byteLength - 22; i >= 0; i--) {
    if (fin.getUint32(i, true) === FIRMA_FIN) { pos = i; break; }
  }
  if (pos < 0) throw new ErrorZip('No es un .zip válido, o está incompleto (¿se terminó de descargar?).');

  let total = fin.getUint16(pos + 10, true);
  let tamCentral = fin.getUint32(pos + 12, true);
  let inicioCentral = fin.getUint32(pos + 16, true);
  if (total === 0xffff || tamCentral === 0xffffffff || inicioCentral === 0xffffffff) {
    // ZIP64: el localizador está justo antes del registro final.
    const absFin = zip.size - cola + pos;
    const loc = await leerBytes(zip, absFin - 20, absFin);
    if (loc.getUint32(0, true) !== FIRMA_LOCALIZADOR64) throw new ErrorZip('El .zip está dañado (ZIP64 sin localizador).');
    const pos64 = u64(loc, 8);
    const f64 = await leerBytes(zip, pos64, pos64 + 56);
    if (f64.getUint32(0, true) !== FIRMA_FIN64) throw new ErrorZip('El .zip está dañado (ZIP64).');
    total = u64(f64, 32);
    tamCentral = u64(f64, 40);
    inicioCentral = u64(f64, 48);
  }

  const central = await leerBytes(zip, inicioCentral, inicioCentral + tamCentral);
  const salida: EntradaZip[] = [];
  let o = 0;
  for (let n = 0; n < total; n++) {
    if (central.getUint32(o, true) !== FIRMA_CENTRAL) throw new ErrorZip('El índice del .zip está dañado.');
    const banderas = central.getUint16(o + 8, true);
    const metodo = central.getUint16(o + 10, true);
    let comprimido = central.getUint32(o + 20, true);
    let tamano = central.getUint32(o + 24, true);
    const largoNombre = central.getUint16(o + 28, true);
    const largoExtra = central.getUint16(o + 30, true);
    const largoComentario = central.getUint16(o + 32, true);
    let local = central.getUint32(o + 42, true);
    const ruta = nombre(new Uint8Array(central.buffer, central.byteOffset + o + 46, largoNombre), (banderas & 0x800) !== 0);

    // Campo extra ZIP64: trae, en orden, solo los valores que no cupieron.
    let e = o + 46 + largoNombre;
    const finExtra = e + largoExtra;
    while (e + 4 <= finExtra) {
      const id = central.getUint16(e, true), largo = central.getUint16(e + 2, true);
      if (id === 0x0001) {
        let p = e + 4;
        if (tamano === 0xffffffff) { tamano = u64(central, p); p += 8; }
        if (comprimido === 0xffffffff) { comprimido = u64(central, p); p += 8; }
        if (local === 0xffffffff) { local = u64(central, p); }
      }
      e += 4 + largo;
    }
    o = finExtra + largoComentario;

    if (ruta.endsWith('/')) continue; // carpeta
    const cifrado = (banderas & 0x1) !== 0;
    salida.push({
      ruta: ruta.replace(/\\/g, '/').normalize('NFC'),
      tamano,
      leer: async () => {
        if (cifrado) throw new ErrorZip('El .zip tiene contraseña.');
        const cab = await leerBytes(zip, local, local + 30);
        if (cab.getUint32(0, true) !== FIRMA_LOCAL) throw new ErrorZip('El .zip está dañado en este archivo.');
        const datos = local + 30 + cab.getUint16(26, true) + cab.getUint16(28, true);
        const trozo = zip.slice(datos, datos + comprimido);
        if (metodo === 0) return trozo;
        if (metodo !== 8) throw new ErrorZip(`Compresión no soportada (${metodo}).`);
        const blob = await new Response(trozo.stream().pipeThrough(new DecompressionStream('deflate-raw'))).blob();
        if (blob.size !== tamano) throw new ErrorZip('El archivo salió incompleto del .zip.');
        return blob;
      },
    });
  }
  return salida;
}
